/* The scenario library, from the Deck's side.
 *
 * The Deck can list the library and run an entry from it; it cannot author one. Authoring is the
 * ORBAT builder's job, and duplicating even a little of it here would create a second place where
 * a scenario can be edited — with two ideas of what a valid one looks like.
 *
 * Running an entry is EXCON-only server-side. This client asks and reports; it never decides.
 */

import { authHeaders, baseUrl } from './gateway'

export interface LibraryScenario {
  slug: string
  name: string
  description: string
  epochIso: string
  classification: string
  units: number
  sides: string[]
  digest: string
  datasetDigest: string
  tickSeconds: number
  /** True for the exercise currently loaded in the Gateway. */
  active: boolean
  /** False when the scenario no longer validates against the loaded dataset. */
  runnable: boolean
  /** Why it is not runnable. Shown verbatim — the usual cause is a dataset that moved on, and the
   *  fix is to re-publish rather than to re-author, which the wording makes clear. */
  problems: string[]
}

export interface Library {
  active: string
  datasetDigest: string
  scenarios: LibraryScenario[]
}

export async function fetchLibrary(): Promise<Library> {
  const res = await fetch(`${baseUrl()}/v1/scenarios`, { headers: authHeaders() })
  if (!res.ok) throw new Error(`HTTP ${res.status}`)
  return (await res.json()) as Library
}

/** Make a library scenario the running exercise. Stops the clock and restarts at tick 0. */
export async function activateScenario(slug: string): Promise<void> {
  const res = await fetch(`${baseUrl()}/v1/scenarios/${encodeURIComponent(slug)}/activate`, {
    method: 'POST',
    headers: authHeaders(),
  })
  if (res.ok) return
  // The Gateway's `detail` carries the reason — a role refusal, or every validation problem at
  // once. Replacing it with the status code would send the operator to the logs for something the
  // server already said.
  let detail = `HTTP ${res.status}`
  try {
    const body = (await res.json()) as { detail?: string }
    if (body.detail) detail = body.detail
  } catch {
    /* non-JSON error body */
  }
  throw new Error(detail)
}
